import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Plus, Pencil, Trash, Users as UsersIcon, MagnifyingGlass,
  EnvelopeSimple, Phone, ShieldCheck, PaperPlaneTilt, Clock, CheckCircle, XCircle, Power,
  Lock, Sparkle
} from '@phosphor-icons/react';

export default function UsersPage() {
  const { user: currentUser, apiCall } = useAuth();
  const navigate = useNavigate();
  const isTrialing = currentUser?.subscription_status === 'trialing';
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', password: '', address: '', mobile_no: '', work_no: '', role: 'regular_user'
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [deleteInvId, setDeleteInvId] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);

  useEffect(() => { loadUsers(); loadInvitations(); loadPlanLimits(); }, []);

  // Quick action intent from CommandPalette
  useEffect(() => {
    const intent = sessionStorage.getItem('roofmate_pending_action');
    if (intent === 'invite-user') {
      sessionStorage.removeItem('roofmate_pending_action');
      setTimeout(() => { setInviteForm({ email: '', name: '' }); setShowInviteDialog(true); }, 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadUsers() {
    try {
      const res = await apiCall('get', '/users');
      setUsers(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function loadInvitations() {
    try {
      const res = await apiCall('get', '/invitations');
      setInvitations(res.data || []);
    } catch (e) { console.error(e); }
  }

  async function loadPlanLimits() {
    if (!currentUser?.tenant_id) return;
    try {
      const res = await apiCall('get', `/billing/check-limits/${currentUser.tenant_id}`);
      setPlanLimits(res.data);
    } catch (e) {
      // Plan limits are non-critical UI hints — don't break the page, but log
      // so we can spot a broken /billing/check-limits endpoint in DevTools.
      console.warn('loadPlanLimits failed:', e);
    }
  }

  async function handleSave() {
    if (!form.name.trim() || !form.email.trim()) return;
    try {
      if (editUser) {
        const { password, role, ...updateData } = form;
        await apiCall('put', `/users/${editUser.id}`, updateData);
      } else {
        if (!form.password) return alert('Password is required');
        await apiCall('post', '/users', form);
      }
      setShowDialog(false); setEditUser(null);
      setForm({ name: '', email: '', password: '', address: '', mobile_no: '', work_no: '', role: 'regular_user' });
      loadUsers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save user');
    }
  }

  async function handleDelete(id) {
    try { await apiCall('delete', `/users/${id}`); setDeleteTarget(null); loadUsers(); } catch (e) { console.error(e); }
  }

  async function handleToggleActive() {
    if (!toggleTarget) return;
    const newStatus = toggleTarget.is_active === false ? true : false;
    try {
      await apiCall('put', `/users/${toggleTarget.id}`, { is_active: newStatus });
      setToggleTarget(null);
      loadUsers();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to update status');
    }
  }

  async function handleInvite() {
    if (!inviteForm.email.trim()) return;
    setInviteLoading(true);
    try {
      await apiCall('post', '/invitations', inviteForm);
      setShowInviteDialog(false);
      setInviteForm({ email: '', name: '' });
      loadInvitations();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send invitation');
    } finally { setInviteLoading(false); }
  }

  async function handleCancelInvite(id) {
    try { await apiCall('delete', `/invitations/${id}`); setDeleteInvId(null); loadInvitations(); } catch (e) { console.error(e); }
  }

  function openEdit(u) {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password: '', address: u.address || '', mobile_no: u.mobile_no || '', work_no: u.work_no || '', role: u.role });
    setShowDialog(true);
  }

  function openNew() {
    setEditUser(null);
    setForm({ name: '', email: '', password: '', address: '', mobile_no: '', work_no: '', role: 'regular_user' });
    setShowDialog(true);
  }

  const roleLabel = (role) => {
    switch (role) { case 'super_admin': return 'Admin'; case 'tenant_admin': return 'Tenant'; default: return 'User'; }
  };
  const roleStyle = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'tenant_admin': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const invStatusBadge = (status) => {
    if (status === 'accepted') return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs"><CheckCircle size={12} className="mr-1" /> Accepted</Badge>;
    if (status === 'expired') return <Badge variant="destructive" className="text-xs"><XCircle size={12} className="mr-1" /> Expired</Badge>;
    return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs"><Clock size={12} className="mr-1" /> Pending</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold" data-testid="users-heading">Team Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {users.length} users, {invitations.filter(i => i.status === 'pending').length} pending invitations
            {planLimits && planLimits.limits.max_users < 999999 && (
              <span className="ml-2 text-xs">({planLimits.usage.users}/{planLimits.limits.max_users} used)</span>
            )}
          </p>
        </div>
        {!currentUser?.is_locked && (
          <div className="flex gap-2">
            <Button onClick={() => setShowInviteDialog(true)} variant="outline" className="rounded-md font-medium" disabled={isTrialing || (planLimits && !planLimits.can_create_user)} data-testid="invite-user-btn">
              {isTrialing ? <Lock size={16} className="mr-2" weight="bold" /> : <PaperPlaneTilt size={16} className="mr-2" weight="bold" />}
              Invite User
            </Button>
            <Button onClick={openNew} className="rounded-md font-medium" disabled={isTrialing || (planLimits && !planLimits.can_create_user)} data-testid="create-user-btn">
              {isTrialing ? <Lock size={16} className="mr-2" weight="bold" /> : <Plus size={16} className="mr-2" weight="bold" />}
              New User
            </Button>
          </div>
        )}
      </div>

      {/* Trial overlay banner — visible to tenants on the free trial */}
      {isTrialing && (
        <div
          className="relative overflow-hidden rounded-lg border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-5 flex items-start gap-4"
          data-testid="trial-users-overlay"
        >
          <div className="w-10 h-10 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Sparkle size={20} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm sm:text-base flex items-center gap-2 mb-1">
              <Lock size={14} weight="fill" className="text-primary" />
              Adding team members is unlocked after your free trial
            </div>
            <p className="text-sm text-muted-foreground">
              While on the free trial, you can explore RoofMate on your own with up to <strong>5 projects</strong>.
              To invite teammates, complete your subscription on the Billing page.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => navigate('/my-billing')}
            className="rounded-md shrink-0"
            data-testid="trial-upgrade-btn"
          >
            Complete subscription
          </Button>
        </div>
      )}

      {/* Plan limit panel — prominent upgrade prompt when paid plan hits its user cap */}
      {planLimits && !planLimits.can_create_user && !isTrialing && (
        <div
          className="relative overflow-hidden rounded-lg border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-5 flex items-start gap-4"
          data-testid="user-limit-upgrade-panel"
        >
          <div className="w-10 h-10 rounded-md bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
            <UsersIcon size={20} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm sm:text-base mb-1">
              You've reached your {planLimits.usage.users}/{planLimits.limits.max_users} user limit
              {planLimits.plan_name ? ` on the ${planLimits.plan_name} plan` : ''}
            </div>
            <p className="text-sm text-muted-foreground">
              {planLimits.next_plan
                ? <>Upgrade to <strong>{planLimits.next_plan.name}</strong> to invite up to <strong>{planLimits.next_plan.max_users}</strong> team members{planLimits.next_plan.price_per_user ? ' (per-user pricing)' : ''}.</>
                : <>Your plan includes <strong>{planLimits.limits.max_users}</strong> seats. Visit Billing to manage your subscription.</>
              }
            </p>
          </div>
          <Button
            onClick={() => navigate('/my-billing')}
            className="rounded-md shrink-0 bg-amber-500 hover:bg-amber-500/90 text-white"
            data-testid="upgrade-plan-btn"
          >
            {planLimits.next_plan ? `Upgrade to ${planLimits.next_plan.name}` : 'Manage plan'}
          </Button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-border">
        <button onClick={() => setTab('users')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'users' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-users">
          Users ({users.length})
        </button>
        <button onClick={() => setTab('invitations')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'invitations' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-invitations">
          Invitations ({invitations.length})
        </button>
      </div>

      {tab === 'users' && (
        <>
          <div className="relative max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..." className="pl-9 rounded h-10" data-testid="users-search-input" />
          </div>

          {filtered.length === 0 ? (
            <Card className="shadow-card rounded-lg border-0">
              <CardContent className="p-12 text-center text-muted-foreground">
                <UsersIcon size={48} className="mx-auto mb-4 opacity-30" />
                <p className="font-semibold text-lg">No users found</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-card rounded-lg border-0 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role</TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Projects</TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact</TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u) => (
                    <TableRow key={u.id} className="hover:bg-secondary/30" data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm"><span className="flex items-center gap-1"><EnvelopeSimple size={12} /> {u.email}</span></TableCell>
                      <TableCell><span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${roleStyle(u.role)}`}>{roleLabel(u.role)}</span></TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{u.project_count || 0}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.mobile_no && <span className="flex items-center gap-1"><Phone size={10} /> {u.mobile_no}</span>}</TableCell>
                      <TableCell>{u.is_active !== false ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs">Active</Badge> : <Badge variant="destructive" className="text-xs">Inactive</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.id !== currentUser?.id && (
                            <Button size="sm" variant="ghost" onClick={() => setToggleTarget(u)}
                              className={`h-8 w-8 p-0 ${u.is_active === false ? 'text-emerald-600 hover:text-emerald-600' : 'text-amber-500 hover:text-amber-500'}`}
                              title={u.is_active === false ? 'Activate' : 'Deactivate'}
                              data-testid={`toggle-user-${u.id}`}>
                              <Power size={14} weight="bold" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openEdit(u)} className="h-8 w-8 p-0" data-testid={`edit-user-${u.id}`}><Pencil size={14} /></Button>
                          {u.id !== currentUser?.id && <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(u)} className="h-8 w-8 p-0 text-destructive hover:text-destructive" data-testid={`delete-user-${u.id}`}><Trash size={14} /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      {tab === 'invitations' && (
        <Card className="shadow-card rounded-lg border-0 overflow-hidden">
          {invitations.length === 0 ? (
            <CardContent className="p-12 text-center text-muted-foreground">
              <PaperPlaneTilt size={48} className="mx-auto mb-4 opacity-30" />
              <p className="font-semibold text-lg">No invitations yet</p>
              <p className="text-sm mt-1">Click "Invite User" to send invitations via email.</p>
            </CardContent>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sent</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((inv) => (
                  <TableRow key={inv.id} className="hover:bg-secondary/30" data-testid={`invitation-row-${inv.id}`}>
                    <TableCell className="text-sm font-medium">{inv.email}</TableCell>
                    <TableCell className="text-sm">{inv.name || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{inv.role === 'tenant_admin' ? 'Admin' : 'User'}</Badge></TableCell>
                    <TableCell>{invStatusBadge(inv.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                    <TableCell className="text-right">
                      {inv.status === 'pending' && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleteInvId(inv.id)} className="h-8 w-8 p-0 text-destructive hover:text-destructive" data-testid={`cancel-invite-${inv.id}`}>
                          <Trash size={14} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* Create/Edit User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editUser ? 'Edit User' : 'New User'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[13px] font-medium">Name</Label><Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="rounded-md" data-testid="user-name-input" /></div>
              <div className="space-y-2"><Label className="text-[13px] font-medium">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="user@company.com" className="rounded-md" data-testid="user-email-input" /></div>
            </div>
            {!editUser && <div className="space-y-2"><Label className="text-[13px] font-medium">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Initial password" className="rounded-md" data-testid="user-password-input" /></div>}
            <div className="space-y-2"><Label className="text-[13px] font-medium">Address</Label><Input value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Address" className="rounded-md" data-testid="user-address-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[13px] font-medium">Mobile No.</Label><Input value={form.mobile_no} onChange={(e) => setForm(p => ({ ...p, mobile_no: e.target.value }))} placeholder="Mobile" className="rounded-md" data-testid="user-mobile-input" /></div>
              <div className="space-y-2"><Label className="text-[13px] font-medium">Work No.</Label><Input value={form.work_no} onChange={(e) => setForm(p => ({ ...p, work_no: e.target.value }))} placeholder="Work phone" className="rounded-md" data-testid="user-work-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} className="rounded-md" data-testid="user-save-btn">{editUser ? 'Save' : 'Create'}</Button>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-md">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader><DialogTitle><PaperPlaneTilt size={20} className="inline mr-2 text-primary" />Invite User via Email</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Email Address</Label>
              <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm(p => ({ ...p, email: e.target.value }))} placeholder="colleague@company.com" className="rounded-md" data-testid="invite-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Name (optional)</Label>
              <Input value={inviteForm.name} onChange={(e) => setInviteForm(p => ({ ...p, name: e.target.value }))} placeholder="Their name" className="rounded-md" data-testid="invite-name-input" />
            </div>
            <p className="text-xs text-muted-foreground">An email invitation will be sent. They'll create their own password when they accept.</p>
          </div>
          <DialogFooter>
            <Button onClick={handleInvite} disabled={inviteLoading || !inviteForm.email.trim()} className="rounded-md" data-testid="send-invite-btn">
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </Button>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)} className="rounded-md">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete User" description={`Delete "${deleteTarget?.name}" (${deleteTarget?.email})? This cannot be undone.`}
        onConfirm={() => handleDelete(deleteTarget?.id)} destructiveLabel="Delete User" />

      <ConfirmDeleteDialog open={!!deleteInvId} onOpenChange={(open) => !open && setDeleteInvId(null)}
        title="Cancel Invitation" description="Cancel this pending invitation?"
        onConfirm={() => handleCancelInvite(deleteInvId)} destructiveLabel="Cancel Invitation" />

      <ConfirmDeleteDialog open={!!toggleTarget} onOpenChange={(open) => !open && setToggleTarget(null)}
        title={toggleTarget?.is_active === false ? 'Activate User' : 'Deactivate User'}
        description={toggleTarget?.is_active === false
          ? `Activate "${toggleTarget?.name}"? They will be able to login again.`
          : `Deactivate "${toggleTarget?.name}" (${toggleTarget?.email})? They will be blocked from logging in. No data will be deleted.`}
        onConfirm={handleToggleActive}
        destructiveLabel={toggleTarget?.is_active === false ? 'Activate' : 'Deactivate'} />
    </div>
  );
}
